import type { DateFlag } from '../flags.js';
import { TALLINN } from '../wire.js';

// Built once: constructing an Intl formatter costs far more than formatting with it, and a
// backfill calls this ~1,850 times.
const TALLINN_DATE = new Intl.DateTimeFormat('sv-SE', {
  timeZone: TALLINN,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

/**
 * `'2026-08-06T14:56:06+03:00'` → `'2026-08-06'`, in Europe/Tallinn.
 *
 * The site stamps posts with its own local offset, but a UTC timestamp (or one from another
 * offset) must still land on the Tallinn calendar day — 2025-12-31T23:30Z is 2026-01-01 here.
 * 'sv-SE' renders as YYYY-MM-DD, which is exactly the storage format.
 */
export function toTallinnDate(iso: string): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) throw new RangeError(`Not a parsable timestamp: ${iso}`);
  return TALLINN_DATE.format(at);
}

/* ─── Incident dates ──────────────────────────────────────────────────────────
 *
 * Police entries date themselves with a day and a month and never a year: `6. augustil…`,
 * `Ajavahemikul 05-06.08…`. Parsing and year resolution are deliberately two functions —
 * {@link parseEstonianDate} reports only what the text says, and {@link resolveOccurredOn} is the
 * single place that ever decides a year, from the post's publication date.
 *
 * Everything below is a plain `YYYY-MM-DD` string in Europe/Tallinn. Dates are compared as
 * strings — zero-padded ISO order is chronological order — and never as `Date` objects in local
 * time, which would make the answer depend on the runner's timezone.
 */

/** How firmly the entry's leading date construct commits to a day. */
export type DateKind = 'exact' | 'range_start' | 'reported_only' | 'unknown';

/** What a resolved `occurred_on` actually means. Stored as `incidents.occurred_precision`. */
export type OccurredPrecision = 'day' | 'range_start' | 'unknown';

export interface ParsedDate {
  /** Day of month as written, 1-31. Not validated against the month — see {@link resolveOccurredOn}. */
  day: number | null;
  /** 1-12. */
  month: number | null;
  /**
   * The year the text stated, and **only** ever that (Epic 6 §2.1).
   *
   * Non-null exclusively from the {@link FULL_DATE} branch: every other construct in this corpus
   * gives a day and a month and no year at all, and a year inferred here would be a year decided
   * outside {@link resolveOccurredOn}. A non-null value therefore means "the paragraph said so",
   * which is what licenses the year bypass downstream.
   */
  year: number | null;
  kind: DateKind;
}

/**
 * The adessive month forms the reports use (`6. augustil` — "on the 6th of August").
 *
 * These are the only forms that appear in the corpus; the nominative (`august`) never leads an
 * entry, so accepting it would only widen the surface for false positives.
 */
export const MONTHS_ET: Readonly<Record<string, number>> = Object.freeze({
  jaanuaril: 1,
  veebruaril: 2,
  märtsil: 3,
  aprillil: 4,
  mail: 5,
  juunil: 6,
  juulil: 7,
  augustil: 8,
  septembril: 9,
  oktoobril: 10,
  novembril: 11,
  detsembril: 12,
});

/** `6. augustil` / `6.augustil`. Built from {@link MONTHS_ET} so the two can never drift apart. */
const NAMED_DAY = new RegExp(`^(\\d{1,2})\\.\\s*(${Object.keys(MONTHS_ET).join('|')})\\b`, 'i');

/**
 * `05-06.08` — first day, second day, month. The separator is a hyphen, an en dash, or a dot,
 * because all three appear in the corpus and look alike on screen.
 */
const NUMERIC_RANGE = /^(\d{1,2})[.\-–](\d{1,2})\.(\d{1,2})\b/;
const PREFIXED_RANGE = /^Ajavahemikul\s+(\d{1,2})[.\-–](\d{1,2})\.(\d{1,2})/i;

/**
 * `12.08.2026` / `12,08.2026` / `12. 08.2026` — the Liiklusinfo opener (Epic 6 §2.1).
 *
 * The tolerance is deliberately `lexicon.ts`'s incident-opener rule, character for character,
 * because it **is** that rule: `isIncidentLine` imports this constant. A line the classifier calls
 * an incident because it starts with a full date must be a line this module can read, and the only
 * way for two regexes not to drift apart is for there to be one.
 *
 * The comma is the typist's (`20,12.2018`, post 326) and the optional space is the CMS's
 * (`12. 08.2026`); both appear in the corpus and both must parse. `(?!\d)` is what keeps a longer
 * digit run from being cut down to a date.
 *
 * ASCII throughout, so the `u` flag the lexicon's copy carried changes nothing here and is omitted
 * to keep this identical to the design's §2.1 listing.
 */
export const FULL_DATE = /^(\d{1,2})[.,]\s?(\d{1,2})\.(\d{4})(?!\d)/;

/**
 * The same opener with the defects the Liiklusinfo typist actually makes, one family per defect.
 *
 * Every entry below is a line the site published; the day and the month are intact in all of them
 * and only the year, or the separator in front of it, came out wrong. They are kept apart from
 * {@link FULL_DATE} rather than folded into it because the two answer different questions: a line
 * matching `FULL_DATE` states a year this module can use, and a line matching one of these states a
 * date a reader can see and a year that is either missing or unusable. The classifier tells them
 * apart the same way, and marks the second kind `date_typo`.
 *
 *  - **The year has the wrong number of digits.** `01.08.223 kella 11.49 ajal juhtus liiklusõnnetus
 *    Tallinnas Mustakivi tee 13 juures …` (post 11411), `25.08.20224 kell 13.33 toimus
 *    liiklusõnnetus Tallinnas Vabaõhumuuseumi tee 75 …` (post 12165), `06.08.206 kella 16.53 ajal
 *    juhtus liiklusõnnetus Jõgevamaal …` (post 13686). Three digits or five; ten such lines.
 *  - **The separator before the year is a space.** `06.09 2020 kella 13.20 ajal juhtus
 *    liiklusõnnetus Viljandimaal …` (post 1684), `04.05 2023 kell 18.55 …` (post 11237),
 *    `12.10 2025 kell 22.49 teatati liiklusõnnetusest Lääne-Virumaal …` (post 13013).
 *  - **The separator is doubled**, or the month carries a zero too many: `03..11.2021 kella 18.49
 *    ajal toimus liiklusõnnetus Harjumaal …` (post 2624), `08.011.2021 kell 11.42 toimus
 *    liiklusõnnetus Saaremaal Kuressaares …` (post 2637).
 *  - **The year is missing altogether.** `02.06 kell 20.44 toimus liiklusõnnetus Ida-Virumaal
 *    Sillamäe linnas …` (post 659). This is the one family that has to say what follows it: a bare
 *    `DD.MM` is also how a date *range* opens (`24.09 – 28.09 Politsei paneb kõigi südamele …`,
 *    post 899, an advisory) and how a decimal number reads, so it is recognised only in front of
 *    the clock reading the Liiklusinfo template always puts there.
 *
 * A year of the wrong length is **not** repaired into the nearest plausible one — `206` is not
 * evidence for 2026 — so those families report no year at all and hand the question to
 * {@link resolveOccurredOn}, which resolves the day and the month against the post exactly as it
 * does for the digest, whose lines never state a year either. The two families whose year is intact
 * keep it.
 */
const MALFORMED_DATES: readonly { pattern: RegExp; yearIsReadable: boolean }[] = [
  { pattern: /^(\d{1,2})[.,]\s?(\d{1,2})\.(?:\d{3}|\d{5,6})(?!\d)/, yearIsReadable: false },
  { pattern: /^(\d{1,2})[.,]\s?(\d{1,2})[.,]?\s+(\d{4})(?!\d)/, yearIsReadable: true },
  { pattern: /^(\d{1,2})\.\.(\d{1,2})\.(\d{4})(?!\d)/, yearIsReadable: true },
  { pattern: /^(\d{1,2})[.,]\s?0(\d{1,2})\.(\d{4})(?!\d)/, yearIsReadable: true },
  { pattern: /^(\d{1,2})[.,]\s?(\d{1,2})(?![\d.])(?=\s+kella?\s+\d)/, yearIsReadable: false },
];

/**
 * True when the line opens on a full date the source mistyped (see {@link MALFORMED_DATES}).
 *
 * Exported for `lexicon.ts`, which claims such a line as an incident in conjunction with a clock
 * reading ({@link STATES_CLOCK}) and never on this test alone: a mistyped year is evidence about
 * one token, and the template these lines belong to states the minute as well as the day.
 */
export function hasMalformedFullDate(text: string): boolean {
  const line = text.trimStart();
  if (FULL_DATE.test(line)) return false;
  return MALFORMED_DATES.some((d) => d.pattern.test(line));
}

const UNPARSED: ParsedDate = Object.freeze({ day: null, month: null, year: null, kind: 'unknown' });

/**
 * The leading date construct of a report line, as day + month (+ year, when the text states one).
 *
 * **Never *infers* a year** — it reports the one the text wrote down, and nothing else. The digest
 * corpus never writes one, so `year` is null there and the year-boundary rule stays in
 * {@link resolveOccurredOn}, which remains the only place a year is ever *decided*. A range yields
 * its FIRST date (`Ajavahemikul 05-06.08`
 * → 5 August): the incident began then, and the second date is only when it was discovered.
 *
 * The day is returned exactly as written and is *not* checked against the month; `31. aprillil`
 * comes back as day 31 of month 4 and is rejected later, where the year is known and the
 * downgrade can be flagged.
 */
export function parseEstonianDate(text: string): ParsedDate {
  // Leading whitespace only — the constructs are anchored to the start of the line, which is
  // what keeps `1975. aastal sündinud` and mid-sentence dates from being read as the date.
  const line = text.trimStart();

  const named = NAMED_DAY.exec(line);
  if (named) {
    const [, day, month] = named;
    // Both groups are mandatory in the pattern, so a match always has them.
    if (day !== undefined && month !== undefined) {
      const m = MONTHS_ET[month.toLowerCase()];
      if (m !== undefined) return { day: Number(day), month: m, year: null, kind: 'exact' };
    }
  }

  // Before the range branch, though the two cannot actually collide: `05.06.08` is a range and
  // `05.06.2008` is a date, and the range's trailing `\b` already refuses a four-digit tail. Tried
  // first anyway, so the precedence is a property of this file rather than of that `\b`.
  const full = FULL_DATE.exec(line);
  if (full) {
    const [, day, month, year] = full;
    if (day !== undefined && month !== undefined && year !== undefined) {
      return { day: Number(day), month: Number(month), year: Number(year), kind: 'exact' };
    }
  }

  // After the well-formed date and before the ranges, so a line the source mistyped is read the
  // way the same line would have been read spelled correctly. `year` stays null wherever the
  // stated year was unreadable, which is the digest's own case and is resolved the digest's way.
  for (const { pattern, yearIsReadable } of MALFORMED_DATES) {
    const m = pattern.exec(line);
    const [, day, month, year] = m ?? [];
    if (day === undefined || month === undefined) continue;
    return {
      day: Number(day),
      month: Number(month),
      year: yearIsReadable && year !== undefined ? Number(year) : null,
      kind: 'exact',
    };
  }

  const range = PREFIXED_RANGE.exec(line) ?? NUMERIC_RANGE.exec(line);
  if (range) {
    const [, first, , month] = range;
    if (first !== undefined && month !== undefined) {
      return { day: Number(first), month: Number(month), year: null, kind: 'range_start' };
    }
  }

  return { ...UNPARSED };
}

const pad = (n: number, width: number): string => String(n).padStart(width, '0');

/**
 * `YYYY-MM-DD`, or `null` when the calendar has no such day.
 *
 * `Date.UTC` happily rolls 31 April into 1 May and 29 February 2026 into 1 March, so the only way
 * to reject an impossible date is to build it and check that all three fields survived the trip.
 * UTC, not local time: this must not depend on where the process runs.
 */
export function ymd(year: number, month: number, day: number): string | null {
  if (!Number.isInteger(year) || !Number.isInteger(month) || !Number.isInteger(day)) return null;
  const at = new Date(Date.UTC(year, month - 1, day));
  if (Number.isNaN(at.getTime())) return null;
  // The round trip also catches the two-digit-year legacy of `Date.UTC`, which maps 26 → 1926.
  if (at.getUTCFullYear() !== year || at.getUTCMonth() !== month - 1 || at.getUTCDate() !== day) {
    return null;
  }
  return `${pad(year, 4)}-${pad(month, 2)}-${pad(day, 2)}`;
}

/**
 * True for a string that is both shaped like `YYYY-MM-DD` and names a day the calendar has.
 *
 * Exported because the API layer needs exactly this question answered — its window bounds go into
 * {@link addDays}, which reads fixed offsets out of the string and so must never be handed one
 * `Date` would roll over or read as `NaN` — and two more copies of the round trip were living in
 * `api/queries.ts` and `api/routes.ts`. One definition, in the module that already owns the
 * calendar, is the only way three call sites cannot answer differently.
 *
 * `ymd` is the test, which also settles the years `Date.UTC` maps into the 20th century: `0032` is
 * not a date this validator accepts, because `addDays('0032-01-01', 1)` would answer `1932-01-02`.
 */
export function isCalendarDate(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const year = Number(value.slice(0, 4));
  const month = Number(value.slice(5, 7));
  const day = Number(value.slice(8, 10));
  return ymd(year, month, day) === value;
}

/**
 * `date` shifted by whole days, still as a `YYYY-MM-DD` string.
 *
 * The shift happens at UTC midnight, where a day is always exactly 24 hours — doing it in local
 * time would land on 23:00 on a DST boundary and silently lose a day. Requires a date that has
 * already passed {@link ymd}; callers here validate first.
 *
 * Exported for the projection layer, whose `report_date = published_date − 1 day` (§5.2) is the
 * same arithmetic and must not grow a second implementation of it.
 */
export function addDays(date: string, days: number): string {
  const at = new Date(
    Date.UTC(
      Number(date.slice(0, 4)),
      Number(date.slice(5, 7)) - 1,
      Number(date.slice(8, 10)) + days,
    ),
  );
  return `${pad(at.getUTCFullYear(), 4)}-${pad(at.getUTCMonth() + 1, 2)}-${pad(at.getUTCDate(), 2)}`;
}

export interface ResolvedDate {
  occurredOn: string | null;
  precision: OccurredPrecision;
  /**
   * Absent — not empty — when there is nothing to say, which is the overwhelming majority of the
   * corpus. A list rather than a single flag because the two facts a date can carry are
   * independent: where its year came from, and how far before publication it landed.
   */
  flags?: readonly DateFlag[];
}

/**
 * How far before publication a resolved date may sit before it is worth a second look.
 *
 * A legitimate backfill batch reaches seven days — the 2018-12-27 sitting covering 20.–25.12 — and
 * a copy-paste year error reaches 365. Thirty separates them cleanly and leaves a flagged tail
 * small enough to read by hand through `cli review --flag stated_date_far`.
 *
 * The threshold is about the *distance*, not about where the year came from, so both branches
 * below measure against it — see {@link resolveOccurredOn} on why one flag serves both.
 */
const STATED_DATE_FAR_DAYS = 30;

/**
 * The stated-year branch of §5.1 as Epic 6 §2.2 amends it: the year the paragraph wrote down, with
 * the subtract-a-year inference **bypassed entirely**.
 *
 * Inference exists only because the digest never states a year. Where the text does, reusing it
 * would silently rewrite exactly the mis-datings this branch is meant to surface — and the research
 * settled (post 2354, headline 2020 against seven paragraphs saying 2021) that the body is the
 * authority. So the two directions are deliberately asymmetric:
 *
 *   - **After publication → dropped.** Police do not report the future. Keeping it would put a
 *     point to the right of the chart's data and break `effective_date ≤ published_date + 1`. One
 *     day of slack, as on the digest path, absorbs a late-evening post crossing midnight.
 *   - **Long before publication → kept and flagged.** The stated date is never rewritten; the flag
 *     is how a reviewer finds the tail, and {@link STATED_DATE_FAR_DAYS} is where the line sits.
 *
 * Precision is `'day'` in every resolvable case, per the design's pseudocode — note that this arm
 * does not consult `dateKind` at all. In this build that costs nothing: the only producer of a year
 * is {@link parseEstonianDate}'s full-date branch, which always reports `'exact'`, so there is no
 * range to preserve. The signature does not *enforce* that pairing, though, so a future caller that
 * hands over a year alongside a `'range_start'` would get `'day'` here and would need this rule
 * revisited rather than worked around.
 */
function fromStatedYear(
  day: number,
  month: number,
  year: number,
  publishedDate: string,
): ResolvedDate {
  const candidate = ymd(year, month, day);
  if (candidate === null) {
    return { occurredOn: null, precision: 'unknown', flags: ['date_mismatch'] };
  }
  if (candidate > addDays(publishedDate, 1)) {
    return { occurredOn: null, precision: 'unknown', flags: ['date_in_future'] };
  }
  if (isFarBefore(candidate, publishedDate)) {
    return { occurredOn: candidate, precision: 'day', flags: ['stated_date_far'] };
  }
  return { occurredOn: candidate, precision: 'day' };
}

/** More than {@link STATED_DATE_FAR_DAYS} before publication. One definition, both branches. */
function isFarBefore(candidate: string, publishedDate: string): boolean {
  return candidate < addDays(publishedDate, -STATED_DATE_FAR_DAYS);
}

/**
 * The day an incident happened, from its day + month and the post's publication date (spec §5.1).
 *
 * The year comes from the post. A candidate later than the day *after* publication cannot be from
 * this year — police do not report the future — so it belongs to the previous one: a post
 * published 2026-01-02 saying `31. detsembril` resolves to 2025-12-31, not a date eleven months
 * away. The one day of slack absorbs a late-evening post that crosses midnight before publishing.
 *
 * The rule is subtract-only, so a date is never pushed *forward* a year.
 *
 * **Every subtraction says so** (audit D3). The inference is a guess the module makes on the
 * corpus's behalf, and it is right for the case it was written for — a 2 January post reporting
 * 31 December — and catastrophically wrong when a post's own `published_date` is out of order with
 * its body: post 13406 is stamped 2026-04-03, says `9. aprillil`, and resolved fourteen incidents
 * 359 days backwards into 2025 — where, that one post, they were more than half of everything the
 * dashboard showed for the year. The date is *not* second-guessed — it stays exactly what it always
 * was, because there is no evidence here for a better one — but `year_inferred_back` now travels
 * with it, and a reviewer can find every such row.
 *
 * `stated_date_far` fires on top of it whenever the answer lands more than
 * {@link STATED_DATE_FAR_DAYS} before publication, on this branch as on the stated-year one. One
 * flag for one threshold, rather than a second name for the same distance: what the two branches
 * disagree about is *provenance*, and provenance is what `year_inferred_back` records beside it. So
 * the 359-day row carries both, an early-January year-boundary row carries only the first, and a
 * post reporting July in October — the year inference never touched — carries only the second.
 *
 * A date the calendar does not have — `29. veebruaril` in a non-leap year, `31. aprillil` — is
 * downgraded to `unknown` rather than rolled over into the next month. The caller flags it
 * (`date_mismatch`) and `effective_date` falls back to the publication date.
 *
 * `statedYear` switches all of that off and hands over to {@link fromStatedYear}: the paragraph
 * said which year it was, so nothing here has to guess. Omitting it — which every digest caller
 * does, because {@link parseEstonianDate} returns a null year for every digest construct — leaves
 * the behaviour above byte-identical.
 */
export function resolveOccurredOn(
  day: number | null,
  month: number | null,
  publishedDate: string,
  dateKind: DateKind,
  statedYear?: number | null,
): ResolvedDate {
  const unknown = { occurredOn: null, precision: 'unknown' } as const;
  if (day === null || month === null) return unknown;

  const year = Number(publishedDate.slice(0, 4));
  // A published date that is not a real `YYYY-MM-DD` makes every comparison below meaningless.
  if (
    ymd(year, Number(publishedDate.slice(5, 7)), Number(publishedDate.slice(8, 10))) !==
    publishedDate
  ) {
    return unknown;
  }

  if (statedYear !== undefined && statedYear !== null) {
    return fromStatedYear(day, month, statedYear, publishedDate);
  }

  let candidate = ymd(year, month, day);
  if (candidate === null) return unknown; // e.g. 29 February in a non-leap year
  const flags: DateFlag[] = [];
  // String comparison: both sides are zero-padded ISO, so lexicographic order is chronological.
  if (candidate > addDays(publishedDate, 1)) {
    candidate = ymd(year - 1, month, day);
    if (candidate === null) return unknown;
    flags.push('year_inferred_back');
  }
  if (isFarBefore(candidate, publishedDate)) flags.push('stated_date_far');

  const precision = dateKind === 'range_start' ? 'range_start' : 'day';
  if (flags.length === 0) return { occurredOn: candidate, precision };
  return { occurredOn: candidate, precision, flags };
}

/* ─── Time of day ─────────────────────────────────────────────────────────────
 *
 * `kell 07.31` / `kella 15.50 ajal` / `kella 12.00 paiku` is deterministic, so it belongs in code
 * and the traffic prompt is never asked for it (Epic 6 §2.3): fewer output tokens, no hallucination
 * surface, and an exact answer. Same reasoning as the rule that the deterministic date parse beats
 * the model's.
 */

/** How firmly the text commits to the minute. `paiku` ("around") is the corpus's one hedge. */
export type TimePrecision = 'exact' | 'approx';

export interface ParsedTime {
  /** Zero-padded 24-hour `HH:MM`, as `incidents.occurred_time` stores it. */
  time: string;
  precision: TimePrecision;
}

/**
 * `kell`/`kella` plus a clock reading, anywhere in the line.
 *
 * The cue word is mandatory and is the whole safety of this pattern: the paragraph opens with
 * `20.12.2018`, which is three numbers separated by dots and would otherwise read as a time. A
 * lookbehind for a letter keeps it a word rather than a prefix (`kellassepp`) and `(?!\d)` refuses a
 * longer digit run.
 *
 * Global, because {@link parseEstonianTime} walks the readings rather than taking the literal first
 * match: a typo'd hour must not stand between the paragraph and the clock it actually states.
 *
 * Not anchored to the start of the line, unlike every date construct above: the time follows the
 * date, and this is source-agnostic on purpose (§2.3) — a digest line that happens to state one
 * gets it too.
 */
const CLOCK_SOURCE = String.raw`(?<!\p{L})kella?\s+(\d{1,2})[.:](\d{2})(?!\d)`;
const CLOCK = new RegExp(CLOCK_SOURCE, 'giu');

/**
 * The same reading, compiled without `g`, for rules that only need to know that the line states a
 * clock at all — `lexicon.ts`'s mistyped-date opener is the one caller.
 *
 * Built from the source `CLOCK` is built from, for the reason {@link FULL_DATE} is imported rather
 * than restated: two spellings of one tolerance would be free to drift, and this one decides
 * whether a line is read as an incident.
 */
export const STATES_CLOCK = new RegExp(CLOCK_SOURCE, 'iu');

/**
 * The words either side of a reading that turn it from a moment into a span or a bound.
 *
 * All four are verbatim corpus constructions: `ajavahemikul kell 8.50-12` (segment 122746),
 * `ajavahemikus kell 7.30-20.20` (123481, thirteen hours wide), `kella 21.30-21.45 vahel` (108818),
 * `kella 15.15 kuni 15.30 ajal` (116547), `pärast kella 12.10` (125072), `enne kella 10.00`
 * (100265). Ten segments across the whole corpus, against 632 that state a clock at all.
 *
 * `SPAN_BEFORE` matches what sits immediately before the cue word, and the `(?<!\p{L})` guard is
 * load-bearing: `pärastlõunal kella 13.20 ajal` (97769) is an ordinary exact reading whose adverb
 * merely begins with `pärast`. `SPAN_AFTER` requires digits after the dash or `kuni`, so ordinary
 * prose (`kell 09.44 – politsei teatas…`) and street numbers do not trip it.
 */
const SPAN_BEFORE = /(?<!\p{L})(?:ajavahemiku\w*|vahemiku\w*|pärast|enne)\s+$/iu;
const SPAN_AFTER = /^\s*(?:[-–—]|kuni\b)\s*\d{1,2}/iu;

/** The hedges: the text names a minute and says the event was near it, not at it. */
const APPROX_BEFORE = /(?<!\p{L})(?:umbes|ligikaudu)\s+$/iu;
const APPROX_AFTER = /^\s*paiku(?![\p{L}])/iu;

/**
 * The time of day a line states, or `null` when it states none (Epic 6 §2.3).
 *
 * The first **readable** reading wins, matching every other parser here: a paragraph narrating a
 * sequence (`kell 07.31 toimus … ja kell 09.44 teatati …`) is dated by when the event began. An
 * out-of-range reading is not clamped — `kell 25.13` is a typo, and inventing 01:13 from it would
 * put a fabricated minute in a column whose entire justification is that it is exact — but nor does
 * it end the search, because a typo is not a statement that the paragraph has no time.
 *
 * **A window or an open bound yields nothing at all.** `time_precision` has two values and neither
 * of them means "the start of a span": `exact` is a minute the text committed to and `approx` is a
 * minute it hedged. Storing the opening reading of `ajavahemikus kell 7.30-20.20` under either
 * would assert a moment the source never claimed, and would assert it by up to thirteen hours. The
 * date layer can keep a range's first day because it has a `range_start` precision to say so; this
 * column has no such value, so the honest answer is no time. Ten corpus segments lose one; none of
 * them ever had a defensible one.
 */
export function parseEstonianTime(text: string): ParsedTime | null {
  CLOCK.lastIndex = 0;
  for (let match = CLOCK.exec(text); match !== null; match = CLOCK.exec(text)) {
    const [whole, hours, minutes] = match;
    if (hours === undefined || minutes === undefined) continue;

    const h = Number(hours);
    const min = Number(minutes);
    if (h > 23 || min > 59) continue; // a typo: step over it and keep looking

    const before = text.slice(0, match.index);
    const after = text.slice(match.index + whole.length);
    if (SPAN_BEFORE.test(before) || SPAN_AFTER.test(after)) return null;

    return {
      time: `${pad(h, 2)}:${pad(min, 2)}`,
      precision: APPROX_BEFORE.test(before) || APPROX_AFTER.test(after) ? 'approx' : 'exact',
    };
  }
  return null;
}
